import { useEffect, useRef, useState } from 'react';
import type { BrigadeStation } from '@duty/shared';

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 8;

/**
 * Reusable searchable station combobox — built standalone (not embedded in
 * any one form) so any future picker of a real, seeded `BrigadeStation`
 * reuses this rather than re-implementing search. Currently backed by the
 * statewide NSW RFS facilities seed (see `web/src/incident/coverage.ts`'s
 * `allStations()`) — the only agency with a real, sourced station dataset
 * today; callers should not pass a fabricated list for other agencies.
 */
export function StationPicker({
  stations,
  onSelect,
  placeholder = 'Search NSW RFS station…',
  ariaLabel = 'Search NSW RFS station',
}: {
  stations: readonly BrigadeStation[];
  onSelect: (station: BrigadeStation) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches =
    q.length >= MIN_QUERY_LENGTH
      ? stations.filter((s) => s.name.toLowerCase().includes(q) || s.region?.toLowerCase().includes(q)).slice(0, MAX_RESULTS)
      : [];

  const selectStation = (s: BrigadeStation) => {
    onSelect(s);
    setQuery('');
    setOpen(false);
    setHighlighted(0);
  };

  return (
    <div ref={ref} className="relative">
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlighted(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            // Accept the highlighted (default: top) match instead of letting the
            // keystroke fall through to the surrounding form's native submit —
            // that silently no-ops (empty callsign) and looks like the search
            // "does nothing" for anyone used to pressing Enter on a result.
            e.preventDefault();
            const chosen = matches[Math.min(highlighted, matches.length - 1)];
            if (chosen) selectStation(chosen);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-autocomplete="list"
        className="w-full rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-sm text-text placeholder:text-muted focus:border-accent/60"
      />
      {open && matches.length > 0 && (
        <ul role="listbox" className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-surface shadow-lg">
          {matches.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => selectStation(s)}
                onMouseEnter={() => setHighlighted(i)}
                className={`block w-full px-2.5 py-1.5 text-left text-xs hover:bg-surface-2 ${i === highlighted ? 'bg-surface-2' : ''}`}
              >
                <span className="font-medium">{s.name}</span>
                {s.region && <span className="ml-1.5 text-muted">{s.region}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q.length >= MIN_QUERY_LENGTH && matches.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-muted shadow-lg">
          No matching NSW RFS station — enter the agency/brigade manually below.
        </div>
      )}
    </div>
  );
}
